import React, {FC, useState, useMemo} from "react";
import AppUtil from "../../util/AppUtil";
import AnalyseComp from "./AnalyseComp";
import TickManager from "../../service/TickManager";
import {ChainPositions} from "../../models/ChainPositions";
import PositionsGraphAnalyser from "./PositionsGraphAnalyser";

const ChainPositionComponent: FC<{
  mp: ChainPositions,
  idx: number,
  expanded: boolean,
  analysedGroup: number|null,
  toggleGroup: (idx: number) => void,
  setAnalysedGroup: (idx: number|null) => void,
}> = ({ mp, idx, expanded, analysedGroup, toggleGroup, setAnalysedGroup }) => {
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showGraphModal, setShowGraphModal] = useState(false);

  // Determine future symbol and price using the first position
  let futureSymbol = mp.future;
  let futurePrice = mp.futurePrice

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIndicator = (field: string) => {
    if (sortField !== field) return '';
    return sortOrder === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedPositions = useMemo(() => {
    if (!sortField) return mp.positions;

    return [...mp.positions].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortField) {
        case 'tradingsymbol':
          aValue = a.tradingsymbol;
          bValue = b.tradingsymbol;
          break;
        case 'itm':
          aValue = a.intrinsic > 0;
          bValue = b.intrinsic > 0;
          break;
        case 'quantity':
          aValue = a.quantity;
          bValue = b.quantity;
          break;
        case 'average_price':
          aValue = a.average_price;
          bValue = b.average_price;
          break;
        case 'moneyIn':
          aValue = a.moneyIn;
          bValue = b.moneyIn;
          break;
        case 'moneyOut':
          aValue = a.moneyOut;
          bValue = b.moneyOut;
          break;
        case 'profit':
          aValue = a.profit;
          bValue = b.profit;
          break;
        case 'turnover':
          aValue = a.turnover;
          bValue = b.turnover;
          break;
        case 'extrinsicBuy':
          aValue = a.extrinsicBuy;
          bValue = b.extrinsicBuy;
          break;
        case 'extrinsicSell':
          aValue = a.extrinsicSell;
          bValue = b.extrinsicSell;
          break;
        case 'intrinsic':
          aValue = a.intrinsic;
          bValue = b.intrinsic;
          break;
        case 'expiry':
          aValue = a.expiry;
          bValue = b.expiry;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [mp.positions, sortField, sortOrder]);

  return (
    <div className="margin-group" key={idx}>
      <div className="margin-summary-row">
        <span><b>Client:</b> {mp.clientId || '-'}</span>
        <span><b>Margin:</b> {AppUtil.getRoundedOff(mp.margin/100000)}L</span>
        <span><b>Interest:</b> {AppUtil.getRoundedOff(mp.interest/100000)}L</span>
        <span><b>Net Position:</b> <span className={mp.netPosition !== 0 ? 'net-position-alert' : ''}>{mp.netPosition}</span></span>
        <span><b>Turnover:</b> {AppUtil.getRoundedOff(mp.turnOver/100000)}L</span>
        <span><b>Profit:</b> {AppUtil.getRoundedOff(mp.profit/100000)}L</span>
        <span><b>Money In:</b> {AppUtil.getRoundedOff(mp.moneyIn/100000)}L</span>
        <span><b>Money Out:</b> {AppUtil.getRoundedOff(mp.moneyOut/100000)}L</span>
        <span><b>Future:</b> {futureSymbol} @ {futurePrice}</span>
        <span><b>Min Expiry:</b> {(() => {
          const minExpiry = Math.min(...mp.positions.map(p => p.expiry));
          const date = new Date(minExpiry);
          const isToday = AppUtil.isToday(minExpiry);
          return <span className={isToday ? 'expiry-soon' : ''}>{date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>;
        })()}</span>
        <button className="expand-btn" onClick={() => toggleGroup(idx)}>
          {expanded ? 'Collapse' : 'Expand'}
        </button>
        {/*<button className="analyse-btn" onClick={() => setAnalysedGroup(analysedGroup === idx ? null : idx)}>*/}
        {/*  {analysedGroup === idx ? 'Hide Analysis' : 'Analyse'}*/}
        {/*</button>*/}
        <button className="analyse-btn" onClick={() => setShowGraphModal(true)}>
          Analyse via Graph
        </button>
      </div>
      {showGraphModal && (
        <PositionsGraphAnalyser
          positions={mp.positions}
          futurePrice={mp.futurePrice}
          onClose={() => setShowGraphModal(false)}
        />
      )}
      {analysedGroup === idx ? (
        <div className="positions-table-container">
          <AnalyseComp positions={mp.positions} />
        </div>
      ) : expanded ? (
        <div className="positions-table-container">
          <table className="positions-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('tradingsymbol')} className="sortable-header">
                  Trading Symbol({sortedPositions.length}){getSortIndicator('tradingsymbol')}
                </th>
                <th onClick={() => handleSort('itm')} className="sortable-header">
                  ITM{getSortIndicator('itm')}
                </th>
                <th onClick={() => handleSort('quantity')} className="sortable-header">
                  Qty({sortedPositions.map(it=>it.quantity).reduce((a,b)=>a+b)}){getSortIndicator('quantity')}
                </th>
                <th onClick={() => handleSort('average_price')} className="sortable-header">
                  Avg Price{getSortIndicator('average_price')}
                </th>
                <th onClick={() => handleSort('moneyIn')} className="sortable-header">
                  Money In{getSortIndicator('moneyIn')}
                </th>
                <th onClick={() => handleSort('moneyOut')} className="sortable-header">
                  Money Out{getSortIndicator('moneyOut')}
                </th>
                <th onClick={() => handleSort('profit')} className="sortable-header">
                  Profit{getSortIndicator('profit')}
                </th>
                <th onClick={() => handleSort('turnover')} className="sortable-header">
                  Turnover{getSortIndicator('turnover')}
                </th>
                <th onClick={() => handleSort('extrinsicBuy')} className="sortable-header">
                  Extrinsic Buy{getSortIndicator('extrinsicBuy')}
                </th>
                <th onClick={() => handleSort('extrinsicSell')} className="sortable-header">
                  Extrinsic Sell{getSortIndicator('extrinsicSell')}
                </th>
                <th onClick={() => handleSort('intrinsic')} className="sortable-header">
                  Intrinsic{getSortIndicator('intrinsic')}
                </th>
                <th onClick={() => handleSort('expiry')} className="sortable-header">
                  Expiry{getSortIndicator('expiry')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedPositions.map((row, i) => (
                <tr key={row.tradingsymbol + '-' + i}>
                  <td
                    title={(() => {
                      const option = TickManager.getOption(row.tradingsymbol);
                      return `Last Price: ${option.miniTick?.lastPrice ?? '-'} | Max Buyer: ${AppUtil.maxBuyer(option)} | Min Seller: ${AppUtil.minSeller(option)}`;
                    })()}
                  >
                    {row.tradingsymbol}
                  </td>
                  <td>{row.intrinsic > 0 ? 'Yes' : 'No'}</td>
                  <td>{row.quantity}</td>
                  <td>{row.average_price.toFixed(2)}</td>
                  <td>{AppUtil.getRoundedOff(row.moneyIn/100000)}L</td>
                  <td>{AppUtil.getRoundedOff(row.moneyOut/100000)}L</td>
                  <td>{AppUtil.getRoundedOff(row.profit/100000)}L</td>
                  <td>{AppUtil.getRoundedOff(row.turnover/100000)}L</td>
                  <td>{Math.abs(row.extrinsicBuy) > 1e9 ? 'NA' : AppUtil.getRoundedOff(row.extrinsicBuy)}</td>
                  <td>{Math.abs(row.extrinsicSell) > 1e9 ? 'NA' : AppUtil.getRoundedOff(row.extrinsicSell)}</td>
                  <td>{AppUtil.getRoundedOff(row.intrinsic)}</td>
                  <td
                    className={(() => {
                      return AppUtil.isToday(row.expiry) ? 'expiry-soon' : '';
                    })()}
                    title={AppUtil.getDate(row.expiry)}
                  >
                    {(() => {
                      const date = new Date(row.expiry);
                      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export default ChainPositionComponent; 
